<template>
  <Teleport to="body">
    <div class="cq-dock" aria-live="polite">
      <TransitionGroup name="cq-slide" tag="div" class="cq-list">

        <div
          v-for="cmd in queue.activeCommands"
          :key="cmd.id"
          class="cq-card"
          :class="{
            'cq-card--success': cmd._succeeded,
            'cq-card--error':   cmd.has_error,
          }"
        >

          <!-- ── Pendente / a executar ─────────────────────────────────────── -->
          <template v-if="!cmd.is_processed">
            <div class="cq-spinner" aria-hidden="true" />
            <div class="cq-body">
              <p class="cq-title">{{ cmd.label ?? cmd.command }}</p>
              <p class="cq-subtitle">
                {{ cmd.is_executing ? 'A executar na máquina…' : 'Na fila de espera…' }}
              </p>
            </div>
          </template>

          <!-- ── Sucesso ───────────────────────────────────────────────────── -->
          <template v-else-if="cmd._succeeded">
            <div class="cq-icon cq-icon--success" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                   stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10" />
                <polyline points="9 12 11 14 15 10" />
              </svg>
            </div>
            <div class="cq-body">
              <p class="cq-title cq-title--success">{{ cmd.label ?? cmd.command }}</p>
              <p class="cq-subtitle">Executado com sucesso</p>
            </div>
            <button class="cq-close" title="Fechar" @click="queue.dismissCommand(cmd.id)">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                   stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </template>

          <!-- ── Erro ─────────────────────────────────────────────────────── -->
          <template v-else-if="cmd.has_error">
            <div class="cq-icon cq-icon--error" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                   stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
              </svg>
            </div>
            <div class="cq-body">
              <p class="cq-title cq-title--error">{{ cmd.label ?? cmd.command }}</p>
              <p class="cq-subtitle cq-subtitle--error">
                {{ cmd.response || 'Ocorreu um erro ao executar o comando.' }}
              </p>
            </div>
            <button class="cq-close cq-close--error" title="Fechar" @click="queue.dismissCommand(cmd.id)">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                   stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </template>

        </div>

      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script setup>
import { useCommandQueueStore } from '@/stores/commandQueue.js'

const queue = useCommandQueueStore()
</script>

<style scoped>
/* Dock fixo no canto inferior direito — não bloqueia a navegação */
.cq-dock {
  position: fixed;
  bottom: 1.25rem;
  right: 1.25rem;
  z-index: 9000;
  pointer-events: none;
}

.cq-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-end;
}

/* Card base */
.cq-card {
  pointer-events: auto;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: #fff;
  border: 1px solid var(--color-border, #e5e7eb);
  border-radius: 0.875rem;
  box-shadow: 0 4px 24px rgba(0,0,0,.12), 0 1px 4px rgba(0,0,0,.06);
  padding: 0.75rem 1rem;
  width: 17rem;
  max-width: calc(100vw - 2.5rem);
}

.cq-card--success {
  border-color: #16a34a;
  border-left: 3px solid #16a34a;
}

.cq-card--error {
  border-color: var(--color-danger, #dc2626);
  border-left: 3px solid var(--color-danger, #dc2626);
}

/* Spinner (pendente) */
.cq-spinner {
  flex-shrink: 0;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 9999px;
  border: 2.5px solid var(--color-border, #e5e7eb);
  border-top-color: var(--color-primary, #1a4f72);
  animation: cq-spin 0.75s linear infinite;
}

@keyframes cq-spin { to { transform: rotate(360deg); } }

/* Ícones de resultado */
.cq-icon {
  flex-shrink: 0;
  width: 1.25rem;
  height: 1.25rem;
}

.cq-icon--success { color: #16a34a; }
.cq-icon--error   { color: var(--color-danger, #dc2626); }

/* Texto */
.cq-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.cq-title {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--color-text, #111827);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cq-title--success { color: #16a34a; }
.cq-title--error   { color: var(--color-danger, #dc2626); }

.cq-subtitle {
  font-size: 0.6875rem;
  color: var(--color-muted, #6b7280);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cq-subtitle--error {
  white-space: normal;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Botão fechar */
.cq-close {
  flex-shrink: 0;
  width: 1.375rem;
  height: 1.375rem;
  padding: 0.1875rem;
  border-radius: 0.375rem;
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--color-muted, #6b7280);
  transition: background 0.15s, color 0.15s;
  line-height: 0;
}

.cq-close:hover         { background: #f3f4f6; color: var(--color-text, #111827); }
.cq-close--error:hover  { background: #fef2f2; color: var(--color-danger, #dc2626); }

/* Transição: entra/sai pela direita */
.cq-slide-enter-active,
.cq-slide-leave-active { transition: opacity 0.22s ease, transform 0.22s ease; }
.cq-slide-enter-from,
.cq-slide-leave-to     { opacity: 0; transform: translateX(1rem); }
</style>
